import * as React from "react";

import { Fieldset, Radio, RadioGroup } from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import styles from "./parameterForms.module.css";
import { useParameterRecord, type SegmentedOption } from "./parameterValues";
import { SegmentedParameter } from "./SegmentedParameter";
import { TextParameter } from "./TextParameter";

export interface TimestampControlProps {
  name: string;
  columnKey: string;
  unitKey: string;
  /** The units, in the spelling the action is sent. */
  units: readonly SegmentedOption[];
  defaultUnit: string;
  /** The column the action reads when none is named; absent when it has none. */
  defaultColumn?: string;
  unitHint?: string;
  /**
   * Offered as a first choice when the action can find the timestamps itself:
   * choosing it clears both keys.
   */
  detect?: { label: string; hint: string };
}

type TimestampMode = "detect" | "column";

/** Which column holds each row's timestamp, and in what unit. */
export function TimestampControl({
  name,
  columnKey,
  unitKey,
  units,
  defaultUnit,
  defaultColumn,
  unitHint,
  detect,
}: TimestampControlProps) {
  const record = useParameterRecord(name);
  const legendId = React.useId();
  const stored: TimestampMode =
    record.get(columnKey) !== undefined || record.get(unitKey) !== undefined
      ? "column"
      : "detect";
  const [picked, setPicked] = React.useState<TimestampMode | null>(null);
  const mode = detect === undefined ? "column" : picked ?? stored;

  const fields = (
    <div className={styles.row}>
      <TextParameter
        name={name}
        parameterKey={columnKey}
        label="Timestamp column"
        placeholder={defaultColumn}
        mono
      />
      <SegmentedParameter
        name={name}
        parameterKey={unitKey}
        title="Timestamp unit"
        hint={unitHint}
        options={units}
        defaultValue={defaultUnit}
      />
    </div>
  );

  if (detect === undefined) {
    return fields;
  }

  return (
    <Fieldset.Root data-testid="param-timestamps">
      <Fieldset.Legend id={legendId}>Timestamps</Fieldset.Legend>
      <RadioGroup<TimestampMode>
        aria-labelledby={legendId}
        value={mode}
        onValueChange={(next) => {
          setPicked(next);
          if (next === "detect") {
            record.write({ [columnKey]: undefined, [unitKey]: undefined });
          }
        }}
      >
        <Radio value="detect" label={detect.label} />
        <Radio value="column" label="Use a column" />
      </RadioGroup>
      {mode === "column" && <div className={styles.nested}>{fields}</div>}
      {mode === "detect" && (
        <>
          <Fieldset.Description>{detect.hint}</Fieldset.Description>
          {/* The column and unit fields are hidden, so their errors show here. */}
          <ParameterError message={record.error(columnKey)} />
          <ParameterError message={record.error(unitKey)} />
        </>
      )}
    </Fieldset.Root>
  );
}
